import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {Divider, FormLabel, Grid, IconButton, Paper} from '@mui/material';
import _ from 'lodash';
import {Line} from 'react-chartjs-2';
import {
    currentMap,
    loadWardPatientsByWardId,
    updateWardPatientsByWardId,
    wardPatients,
} from '../../store/reducers/ward';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment/moment';
import 'moment-timezone'; // Import moment-timezone for timezone support
import {arrivalModes, getEcdsVersion, referredToService} from '../../store/reducers/dicts';
import API from '../../app/api';
import useOpenState from '../../hooks/useOpenState';
import AgyleDialog from '../../components/modal/agyleDialog';
import PriorityPatients from '../ward/priority';
import MessageBox from '../../util/messageBox';
import clsx from 'clsx';
import {useTheme} from '@mui/material/styles';
import AgyleHeader from '../../components/header/agyleHeader/agyleHeader';
import AgyleToggleButtons from '../../components/ui/buttons/agyleToggleButtons';
import AgyleDatePicker from '../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../components/ui/buttons/agyleButton';
import ClearIcon from '@mui/icons-material/Clear';
import {setSnackbar} from '../../store/reducers/globals';
import {useHistory} from 'react-router-dom';
import {getZoneAndBedsNameFromOldMapUHD} from '../../util/oldMapUtil';
import {hasPermission, Permissions, sendUserAudit} from '../../store/reducers/user';
import {setPatient} from '../../store/reducers/patient';
import {isCentralPageModuleEnabled} from '../centralPage/helper';
import {CENTRAL_PAGE_PATH} from '../centralPage/constants';
import ContextViewer from '../patient/patientContext/contextViewer';

import {getPatientFlagValue} from '../ward/mainScreen/util/patientFlagsUtil';
import {getConfig} from '../../util/configUtil';
import { AUDIT_TYPES } from '../../util/auditUtil';
import APIReports from '../../app/apiReports';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'hidden',
        backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#222425',
    },
    banner: {
        padding: 5,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'auto',
        fontWeight: 'bold',
        fontSize: 'large',
        textAlign: 'center',
        marginRight: 10,
    },
    logo: {
        textAlign: 'right',
        '& img': {
            width: '50px',
        },
    },
    toggle: {
        width: 100,
        marginLeft: 5,
    },
    topStatisticGroup: {
        display: 'inline-flex',
        justifyContent: 'space-between',
        width: '100%',
        padding: '5px 5px',
        marginBottom: 20,
    },
    topStatisticTitle: {
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 5,
    },
    topStatisticSubtitle: {
        fontSize: 'small',
        textAlign: 'center',
    },
    statisticBlock: {
        cursor: 'pointer',
        color: '#000',
        width: '100%',
        minHeight: 20,
        backgroundColor: '#FFF',
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 5,
        border: `1px solid ${theme.palette.mode === 'light' ? '#858687' : '#FFFFFF'}`,
    },
    statisticContainerGroup: {
        backgroundColor: theme.palette.background.paper,
        padding: 10,
    },
    statisticContainer: {
        border: '1px solid grey',
        borderRadius: 5,
        padding: 10,
        height: '95%',
    },
    statisticTitle: {
        fontSize: 15,
        paddingLeft: 5,
    },
    statisticTopic: {
        textAlign: 'center',
        fontSize: 12,
        paddingLeft: 5,
    },
    separator: {
        marginTop: 5,
    },
    tittle: {
        fontWeight: 'bold',
        fontSize: 'x-large',
        marginBottom: 10,
        textAlign: 'center',
    },
    chart: {
        border: '1px solid black',
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'light' ? 'white' : theme.palette.background.paper,
        padding: 2,
    },
    chartText: {
        padding: 5,
        fontWeight: 'bold',
    },
    paper: {
        backgroundColor: theme.palette.background.default,
        margin: 10,
        borderRadius: 5,
        padding: 10,
    },
    label: {
        color: theme.palette.mode === 'light' ? '#000' : '#FFF',
        fontWeight: 'bold',
        margin: '10px 0',
    },
    rowDiv: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    escalationLevel: {
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#262626',
        '&.normal': {
            backgroundColor: '#92d050',
        },
        '&.moderate': {
            backgroundColor: '#ffc000',
        },
        '&.severe': {
            backgroundColor: '#ff0000',
            color: '#fff',
        },
        '&.extreme': {
            backgroundColor: '#7030a0',
            color: '#fff',
        },
    },
    chartBlock: {
        width: '30%',
        marginLeft: 'auto',
    },
    redButton: {
        backgroundColor: '#ff0000',
        color: '#fff',
    },
    disabledField: {
        backgroundColor: theme.palette.mode === 'light' ? '#f2f2f2' : '#434343',
        borderColor: theme.palette.mode === 'light' ? '#858687' : '#434343',
    },
}));

export const ESCALATION_LEVEL = {
    normal: 'normal',
    moderate: 'moderate',
    severe: 'severe',
    extreme: 'extreme',
};

const snomedAllowed = [
    '183516009',
    '183522000',
    '183542009',
    '183545006',
    '306128002',
];

const ExtendedDashboard = () => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const controller = new AbortController();
    const theme = useTheme();
    const history = useHistory();

    // Configs
    const hospitals = getConfig('modules.map.customMapConfig', []);
    const extendedDashboardLineWidth = getConfig('modules.reports.extendedDashboardLineWidth', 2);
    const dashboardTimeout = getConfig('dashboard.timeout', 30000);
    const defaultTimezone = getConfig('defaultTimezone', 'Europe/London');
    const customMap = getConfig('modules.map.customMap', false);
    const dischargeListIdsToCancelInStatistic = getConfig('dischargeListIdsToCancelInStatistic', []);

    // Historic
    const [dateToggle, setDateToggle] = useState('today');

    // Live pt helper stare
    const refContainer = useRef(null);
    const refContainerElement = useRef(null);
    const [zones, setZones] = useState([]);
    const [formattedZones, setFormattedZones] = useState([]);

    // Selectors
    const wardPatientsObject = useSelector(wardPatients);
    const dictArrivalMode = useSelector(arrivalModes);
    const newCurrentMap = useSelector(currentMap);
    const dictReferredToService = useSelector(referredToService);
    const version = dispatch(getEcdsVersion(null));

    // List
    const [isPriorityOpen, openPriority, closePriority] = useOpenState(false, false);
    const [isEmptyListNotificationOpen, openEmptyListNotification, closeEmptyListNotification] = useOpenState(false, false);
    const [patients, setPatients] = useState([]);

    // Stats state
    const [data, setData] = useState(null);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [ambulanceCodes, setAmbulanceCodes] = useState([]);
    const [referredOpt, setReferredOpt] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toggleDate, setToggleDate] = useState(null);
    const [startRange, setStartRange] = useState(null);
    const [endRange, setEndRange] = useState(null);

    // Open Patient
    const [isPatientContextOpen, openPatientContext, closePatientContext] = useOpenState(false, true);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const canOpenPatient = dispatch(hasPermission(Permissions.AGYLE_CAN_OPEN_PATIENT_RECORD));

    // Risk Level
    const [escalationLevel, setEscalationLevel] = useState(null);
    const [globalEscalationLevel, setGlobalEscalationLevel] = useState({
        pht: ESCALATION_LEVEL.normal,
        rbh: ESCALATION_LEVEL.normal,
    });

    // Load current patients and zones info
    useEffect(() => {
        const wardsIds = [];
        const UPDATE_FREQUENCY = 3000; // 3 sec
        let interval;

        // Create wards id for all maps
        _.forEach(hospitals, hosp => {
            wardsIds.push(`ED##${hosp?.hospitalId}`);
        });

        // Load zones info
        getZoneAndBedsNameFromOldMapUHD(refContainer, refContainerElement)
            .then(data => {
                setZones(data);
            });

        // Init Load current pts
        dispatch(loadWardPatientsByWardId(wardsIds, false, true));

        // Check for update
        const updateData = () => dispatch(updateWardPatientsByWardId(wardsIds, true, controller.signal));
        interval = setInterval(updateData, UPDATE_FREQUENCY);

        return () => {
            clearInterval(interval);
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (!_.isEmpty(dictArrivalMode)) {
            const arr = [];
            _.forEach(dictArrivalMode, dict => {
                if (dict?.group === 'ambulance' || dict?.ambulance) arr.push(dict.__code);
            });

            setAmbulanceCodes(arr);
        }

        if (!_.isEmpty(dictReferredToService)) {
            const opt = [];

            _.forEach(_.filter(dictReferredToService, {ecdsVersion: version}), ref => {
                if (snomedAllowed.includes(ref.snomedCode)) {
                    opt.push({
                        id: ref?.snomedCode,
                        name: ref?.__description || '',
                    });
                }
            });

            opt.push({
                id: null,
                name: 'Other',
            });

            setReferredOpt(opt);
        }
    }, [dictArrivalMode, dictReferredToService]);

    // Format zones
    useEffect(() => {
        const format = [];
        _.forEach(zones, zone => {
            const objZone = {
                areaTitle: zone.areaTitle,
                dataName: zone.dataName,
                beds: [],
                hospital: zone.hospital,
            };

            _.forEach(zone.beds, bed => {
                objZone.beds.push(bed.parsed.params[1]);
            });

            format.push(objZone);
        });
        setFormattedZones(format);
    }, [zones]);

    // Get timeline stats
    useEffect(() => {
        let cancel = false;
        const timeout = dashboardTimeout;
        const timezone = defaultTimezone;
        const intervalCheck = async () => {
            let startDate;
            let endDate;

            if (toggleDate) {
                startDate = moment.tz(moment(startRange), timezone).format();
                endDate = moment.tz(moment(endRange), timezone).format();
            } else {
                startDate = dateToggle === 'today'
                    ? moment.tz(timezone).startOf('day').format()
                    : moment.tz(timezone).subtract(1, 'days').format();
                endDate = moment.tz(timezone).format();
            }

            setLoading(true);
            let data = {};
            let chartData = {
                pht: null,
                rbh: null,
            };

            if (customMap) {
                let response;

                for (const hospital of hospitals) {
                    response = await API.getTimelineStatistic(startDate, endDate, [`ED##${hospital.hospitalId}`], controller.signal);
                    if (cancel) return;
                    if (response?.status === 200) {
                        chartData[hospital.shortName.toLowerCase()] = response.data;
                    }
                }

                if (!_.isEmpty(formattedZones)) {
                    const utcBeds = getBedsFromZones(['Sub Wait', 'AWAITINGTriage UTC', 'POST TRIAGEUTC', 'Post TriageUTC', 'Child Wait UTC ', 'UTC WAITINGPre Triage', 'TRIAGE UTC', 'EXAM ROOM',
                         'TREAT ROOM', 'CONSULT A', 'CONSULT B', 'PLASTER', 'MINORS', 'AUTC', 'AUTC SubWait', 'UTCWaiting Room', 'CED']);
                    const excludeUtcBeds = ['nrbhced5(MH)', 'nrbhced6(MH)', 'nrbhced7', 'nrbhced8', 'nrbhced9', 'nrbhced10', 'nrbhced11'];
                    const utcBedsFiltered = utcBeds.filter(bed => !excludeUtcBeds.includes(bed));

                    const majorsBeds = getBedsFromZones(['MAJORS A', 'MAJORS B', 'MINORS A', 'PITSTOP', 'AMBULANCEQueue', 'AMBULANCECohort List', 'CORRIDORList', 'Obs A', 'Obs B', 'RATs', 'AMBULANCEList',
                         'MAJ Interview', 'MAJORS', 'RATS', 'RESUS', 'InitialAssessment', 'AwaitingTriage', 'AmbulanceWait', 'AmbulanceCorridor', 'UHDCohort', 'MajorsWait', 'MAJ Interview', 'Iso 1',
                        'Body Viewing', 'Adult Triage', 'CED Triage', 'CED', 'Children’sPost Triage', 'Children’sSubwait']);
                    const excludeMajorsBeds = ['nrbhced1', 'nrbhced2', 'nrbhced4', 'nrbhcedPlaster', 'nrbhcedTreat'];
                    const majorsBedsFiltered = majorsBeds.filter(bed => !excludeMajorsBeds.includes(bed));
                        
                    const acaBeds = getBedsFromZones(['ACAWaiting', 'ChildrenWaiting', 'ACA', 'PostTriage', 'MAJORS C/E', 'ACA Wait']);

                    for (const hospital of hospitals) {
                        try {
                            response = await APIReports.getLiveDashboardData(startDate, endDate, [`ED##${hospital.hospitalId}`], majorsBedsFiltered, utcBedsFiltered, acaBeds, controller.signal);                            
                        } catch (error) {
                            dispatch(setSnackbar({type: 'error', message: 'An error occurred while loading data.'}));
                        }
                        
                        if (cancel) return;
                        if (response) {
                            data = {
                                ...data,
                                [hospital.shortName.toLowerCase()]: response,
                            };
                        }
                    }
                }
            } else {
                let response;
                response = await API.getStatistic(startDate, endDate, ['ED'], controller.signal);
                if (cancel) return;
                if (response?.status === 200) {
                    data = response.data;
                }

                response = await API.getTimelineStatistic(startDate, endDate, ['ED'], controller.signal);
                if (cancel) return;
                if (response?.status === 200) {
                    chartData.ed = response.data;
                }
            }

            return {
                stats: data,
                chart: chartData,
            };
        };

        const check = () => {
            intervalCheck().then(response => {
                    if (response) {
                        setDashboardStats({
                            ...response.stats,
                        });

                        setChartData(response.chart);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        };
        const interval = setInterval(check, timeout || 30000);

        check();

        return () => {
            cancel = true;
            clearInterval(interval);
        };
    }, [dateToggle, newCurrentMap, toggleDate, formattedZones, startRange, endRange]);

    useEffect(() => {
        let interval;
        if (!_.isEmpty(formattedZones)) {
            getData();
            interval = setInterval(() => {
                getData();
            }, 5000);
        }
        return () => {
            clearInterval(interval);
        };
    }, [ambulanceCodes, formattedZones, wardPatientsObject, referredOpt]);

    // Calculate escalation level for fields and global escalation level
    useEffect(() => {
        if (data) {
            const pht = calculateEscalationLevel('pht');
            const rbh = calculateEscalationLevel('rbh');
            setEscalationLevel({
                pht: pht.innerLevel,
                rbh: rbh.innerLevel,
            });

            setGlobalEscalationLevel({
                pht: pht.global,
                rbh: rbh.global,
            });
        }
    }, [data]);

    const getData = () => {
        const pth = _.filter(wardPatientsObject, pt => pt.facility === 'R0D01');
        const rbh = _.filter(wardPatientsObject, pt => pt.facility === 'R0D02');

        setData({
            pht: updateData(pth, 'pht'),
            rbh: updateData(rbh, 'rbh'),
        });
    };

    const updateData = (pts, hospital) => {
        // Top panel
        const total = [];
        const over4 = [];
        const over12 = [];
        let longestPt = {id: null, date: null};

        // Waiting panel
        const waitingRoom = [];
        let waitingRoomLongest = {id: null, date: null};

        const waitTriage = [];
        let waitTriageLongest = {id: null, date: null};

        const ambulanceQueue = [];
        let ambulanceQueueLongest = {id: null, date: null};

        const ambulanceCohort = [];
        let ambulanceCohortLongest = {id: null, date: null};

        // let hoursLost = [];
        const averageHandover = [];

        const corridor = [];
        let corridorLongest = {id: null, date: null};

        const waitTriageUTC = [];
        let waitTriageUTCLongest = {id: null, date: null};

        // Clinical Process panel
        const waitingSeen = [];
        let waitingSeenLongest = {id: null, date: null};

        const totalMajors = [];
        let totalMajorsLongest = {id: null, date: null};

        const waitingSeenACA = [];
        let waitingSeenACALongest = {id: null, date: null};

        const totalACA = [];
        let totalACALongest = {id: null, date: null};

        const delayed = [];
        let delayedLongest = {id: null, date: null};

        const waitingSeenUTC = [];
        let waitingSeenUTCLongest = {id: null, date: null};

        const totalUTC = [];
        let totalUTCLongest = {id: null, date: null};

        const cap = {
            majors: 0,
            aca: 0,
            paeds: 0,
            resus: 0,
        };

        // Flow Out panel
        const waitBed = [];
        let waitBedLongest = {id: null, date: null};
        const performance4H = [];
        const decisionTimeMajors = [];
        const decisionTimeUTC = [];
        const referredTo = {};
        const leftWithoutBeingSeen = [];

        _.forEach(referredOpt, opt => {
            referredTo[opt.name] = [];
        });

        cap.majors = 20;
        cap.aca = hospital === 'pht' ? 15 : 18;
        cap.paeds = hospital === 'pht' ? 4 : 3;
        cap.resus = 4;

        try {
            _.forEach(pts, element => {
                let elementHidden = false;
                if (_.startsWith(element?.bed, 'wlist') || !element?.bed) {
                    if (element.admission?.locationDescription === 'DISCHARGE') {
                        elementHidden = true;
                    }
                }

                if (!elementHidden && element?.admission?.status !== 'DISCHARGE_REQUESTED') {
                    /*
                     *  TOP PANEL STATS
                     */

                    if (!dischargeListIdsToCancelInStatistic || !dischargeListIdsToCancelInStatistic.includes(element.bed)) {
                        // Add over 4 hours
                        if (moment().diff(element?.admission?.startDate, 'minutes') > 240) {
                            over4.push(element);
                        }
                        // Add over 12 hours
                        if (moment().diff(element?.admission?.startDate, 'minutes') > 720) {
                            over12.push(element);
                        }

                        // Total
                        total.push(element);
                        // Longest Attendance
                        checkLongestAwait(longestPt, element);
                    }

                    // Capacity
                    if (patientInZones(['MAJORS A', 'MAJORS B', 'MINORS A', 'PITSTOP', 'AMBULANCEQueue',
                        'Obs A', 'Obs B', 'RATs', 'Wait Triage', 'CORRIDORList', 'AMBULANCECohort List', 'AMBULANCEList',
                        'MAJORS', 'MINORS', 'RATS'
                    ], element)) {
                        cap.majors -= 1;
                    }

                   const EXCLUDE_CED_CHILD = ["nrbhced1", "nrbhced2", "nrbhced4", "nrbhced5(MH)", "nrbhced6(MH)", "nrbhced10", "nrbhced11"];
                    if (patientInZones(['CHILDREN', 'CHILDRENS', 'Child Wait', 'CED', 'Children’sPost Triage', 'Children’sWaiting Room', 'Children’sSubwait', 'CED Triage'], element) 
                        && !EXCLUDE_CED_CHILD.includes(element?.bed)) { 
                        cap.paeds -= 1;
                    }
                    if (patientInZones(['RESUS'], element)) {
                        cap.resus -= 1;
                    }
                    if (patientInZones(['ACAWaiting', 'ChildrenWaiting', 'ACA', 'PostTriage', 'ACA Wait'], element)) {
                        cap.aca -= 1;
                    }

                    if (patientInZones(['ACAWaiting', 'ChildrenWaiting', 'MajorsWait', 'UTCWaiting Room', 'AwaitingTriage', 'InitialAssessment', 'Children’sWaiting Room', 'Children’sPost Triage'], element)) {
                        waitingRoom.push(element);
                        checkLongestAwait(waitingRoomLongest, element);
                    }

                    if (patientInZones(['WaitTriage', 'Wait Triage', 'AwaitingTriage'], element)) {
                        if (!element?.triage?.discriminator && element?.studies?.seeAndTreat?.status !== 'completed') {
                            waitTriage.push(element);
                            checkLongestAwait(waitTriageLongest, element);
                        }
                    }

                    if (patientInZones(['AMBULANCEQueue', 'AMBULANCEList', 'AmbulanceWait'], element)) {
                        ambulanceQueue.push(element);
                        checkLongestAwait(ambulanceQueueLongest, element);
                    }

                    if (patientInZones(['AMBULANCECohort List', 'AmbulanceCorridor'], element)) {
                        ambulanceCohort.push(element);
                        checkLongestAwait(ambulanceCohortLongest, element);
                    }

                    if (patientInZones(['CORRIDORList', "UHDCohort"], element)) {
                        corridor.push(element);
                        checkLongestAwait(corridorLongest, element);
                    }

                    const EXCLUDE_CED_UTC = ["nrbhced5(MH)", "nrbhced6(MH)", "nrbhced7", "nrbhced8", "nrbhced9", "nrbhced10", "nrbhced11"];
                    if (patientInZones(['Sub Wait', 'AWAITINGTriage UTC', 'POST TRIAGEUTC', 'Post TriageUTC', 'Child Wait UTC ', 'UTC WAITINGPre Triage'
                        , 'TRIAGE UTC', 'EXAM ROOM', 'TREAT ROOM', 'CONSULT A', 'CONSULT B', 'PLASTER', 'MINORS', 'UTCWaiting Room', 
                        'AUTC SubWait', 'AUTC', 'AUTC room', 'CED', "Children’sWaiting Room"], element)) {
                        if (!EXCLUDE_CED_UTC.includes(element?.bed)) {
                            if (!element?.triage?.discriminator && !element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && !element?.firstClinician?.who) {
                                waitTriageUTC.push(element);
                                checkLongestAwait(waitTriageUTCLongest, element);
                            }

                            totalUTC.push(element);
                            checkLongestAwait(totalUTCLongest, element);

                            if (!element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && _.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                                waitingSeenUTC.push(element);
                                checkLongestAwait(waitingSeenUTCLongest, element);
                            }
                        }
                    }

                    const EXCLUDE_CED = ["nrbhced1", "nrbhced2", "nrbhced4", "nrbhcedPlaster", "nrbhcedTreat"];
                    if (patientInZones(['CORRIDORList', 'AMBULANCEQueue', 'AMBULANCECohort List', 'AMBULANCEList', 'RESUS', 'MAJORS A', 'MAJORS B', 'MINORS A', 'MH', 'M/h', 'Obs A', 'Obs B', 'WaitTriage'
                        , 'Wait Triage', 'CHILDRENS', 'RATs', 'PITSTOP', 'TRIAGE', 'Child Wait', 'BUTTERFLY', 'CHILDREN', 'MAJORS', 'MAJ Interview', 'MINORS', 'CED', 'Children’sPost Triage'
                        , 'InitialAssessment', 'AwaitingTriage', 'AmbulanceWait', 'AmbulanceCorridor', 'UHDCohort', 'MajorsWait', 'RATS', 'MAJ Interview', 'Iso 1'
                        , 'Children’sPost Triage', 'Children’sSubwait' ,'CED'], element)) {
                        if (!EXCLUDE_CED.includes(element?.bed)) {
                            totalMajors.push(element);
                            checkLongestAwait(totalMajorsLongest, element);

                            if (!element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && !element?.seniorReview?.who && _.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                                waitingSeen.push(element);
                                checkLongestAwait(waitingSeenLongest, element);
                            }
                        }
                    }

                    if (patientInZones(['ACAWaiting', 'ChildrenWaiting', 'ACA', 'PostTriage', 'ACA Wait', 'MAJORS C/E'], element)) {
                        totalACA.push(element);
                        checkLongestAwait(totalACALongest, element);

                        if (!element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && !element?.seniorReview?.who && _.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                            waitingSeenACA.push(element);
                            checkLongestAwait(waitingSeenACALongest, element);
                        }
                    }

                    if (element?.studies?.bedRequest) {
                        const form = element?.studies?.bedRequest;
                        if ((form.allocated || form.requested) && !(form.canceled || form.ready)) {
                            waitBed.push(element);
                            checkLongestBedAwait(waitBedLongest, element);

                            if (element?.studies?.referrals) {
                                let complete = false;
                                _.forEach(element?.studies?.referrals?.ReferralsToOtherServices, ref => {
                                    if (complete) return; // Show only first referral

                                    if (snomedAllowed.includes(ref?.ReferredToService_SnomedCt?.snomedCode)) {
                                        referredTo[ref.ReferredToService_SnomedCt.__description]?.push(element);
                                        complete = true;
                                    } else {
                                        referredTo['Other']?.push(element);
                                        complete = true;
                                    }
                                });
                            }
                        }
                    }

                    // Exclude Patients in Discharge LIst from Longest Diagnosis Time and Awaiting Diagnosis
                    if (!patientInZones(['Discharge', 'DISCHARGE(Max 10)', 'Discharge /Planned Review', ], element) && _.isEmpty(element?.diagnosis?.primary) && (element?.studies?.seeAndTreat?.when || element?.currentClinician?.timestamp || element?.seniorReview?.when)) {
                        if (element?.facility === 'R0D01' || (element?.facility === 'R0D02' && patientInZones(['AmbulanceWait', 'AmbulanceCorridor', 'UHDCohort', 'MajorsWait', 'RATS', 'RESUS'
                            , 'MAJORS A', 'MAJORS B', 'MAJ Interview', 'MAJORS C/E', 'Iso 1', 'Children’sWaiting Room', 'Children’sPost Triage', 'Children’sSubwait', 'CED'], element) && !EXCLUDE_CED.includes(element?.bed))) {
                            let start;
                            if (element?.studies?.seeAndTreat?.when) start = moment(element?.studies?.seeAndTreat?.when);
                            if (element?.currentClinician?.timestamp) {
                                if (!start || (start && moment.isMoment(start) && start?.isBefore(moment(element?.currentClinician?.timestamp)))) start = moment(element?.currentClinician?.timestamp);
                            }
                            if (element?.seniorReview?.when) {
                                if (!start || (start && moment.isMoment(start) && start?.isBefore(moment(element?.seniorReview?.when)))) start = moment(element?.seniorReview?.when);
                            }

                            if (moment().diff(start, 'minutes') > 60) {
                                delayed.push(element);
                                checkLongestAwait(delayedLongest, element);
                            }
                        }
                    }
                }
            });
        } catch (e) {
            console.warn(e);
            dispatch(setSnackbar({type: 'error', message: 'An error occurred while loading data.'}));
        }

        transformPrettyDateFormat(longestPt);
        transformPrettyDateFormat(waitingRoomLongest);
        transformPrettyDateFormat(waitTriageLongest);
        transformPrettyDateFormat(ambulanceQueueLongest);
        transformPrettyDateFormat(ambulanceCohortLongest);
        transformPrettyDateFormat(corridorLongest);
        transformPrettyDateFormat(waitTriageUTCLongest);
        transformPrettyDateFormat(waitingSeenLongest);
        transformPrettyDateFormat(totalACALongest);
        transformPrettyDateFormat(totalMajorsLongest);
        transformPrettyDateFormat(waitingSeenACALongest);
        transformPrettyDateFormat(delayedLongest);
        transformPrettyDateFormat(waitingSeenUTCLongest);
        transformPrettyDateFormat(totalUTCLongest);
        transformPrettyDateFormat(waitBedLongest);

        // let hlTime = 0;
        // const hoursLostPatients = [];
        // _.forEach(hoursLost, hl => {
        //     const lastBed = _.last(hl?.metadata?.bedHistory);
        //     if (lastBed) {
        //         const mins = moment().diff(lastBed?.when, 'minutes');
        //         if (mins > 15) {
        //             hlTime += (mins - 15);
        //             hoursLostPatients.push(hl);
        //         }
        //     }
        // });
        // hoursLost = transformPrettyDateFormat(hlTime, true);

        return {
            total,
            over4,
            over12,
            longestPt,
            waitingRoom,
            waitingRoomLongest,
            waitTriage,
            waitTriageLongest,
            ambulanceQueue,
            ambulanceQueueLongest,
            ambulanceCohort,
            ambulanceCohortLongest,
            averageHandover,
            corridor,
            corridorLongest,
            waitTriageUTC,
            waitTriageUTCLongest,
            waitingSeen,
            waitingSeenLongest,
            totalMajors,
            totalMajorsLongest,
            totalACA,
            totalACALongest,
            waitingSeenACA,
            waitingSeenACALongest,
            delayed,
            delayedLongest,
            waitingSeenUTC,
            waitingSeenUTCLongest,
            totalUTC,
            totalUTCLongest,
            cap,
            waitBed,
            waitBedLongest,
            performance4H,
            decisionTimeMajors,
            decisionTimeUTC,
            referredTo,
            leftWithoutBeingSeen,
        };
    };

    const calculateEscalationLevel = (hospital) => {
        const datas = data?.[hospital];
        let global = ESCALATION_LEVEL.normal;
        let innerLevel = {
            waitTriage: ESCALATION_LEVEL.normal,
            waitTriageUTC: ESCALATION_LEVEL.normal,
            ambulanceWait: ESCALATION_LEVEL.normal,
            ambulanceCorridorWait: ESCALATION_LEVEL.normal,
            ambulance: ESCALATION_LEVEL.normal,
            ambulanceCorridor: ESCALATION_LEVEL.normal,
            corridor: ESCALATION_LEVEL.normal,
            waitingSeen: ESCALATION_LEVEL.normal,
            waitingSeenTime: ESCALATION_LEVEL.normal,
            waitingSeenACA: ESCALATION_LEVEL.normal,
            waitingSeenACATime: ESCALATION_LEVEL.normal,
            waitingSeenUTC: ESCALATION_LEVEL.normal,
            waitingSeenUTCTime: ESCALATION_LEVEL.normal,
            waitBed: ESCALATION_LEVEL.normal,
            waitBedTime: ESCALATION_LEVEL.normal,
            spaces: ESCALATION_LEVEL.normal,
        };

        // Ambulance Queue Longest
        const ambulanceQueueLongest = getMinutesFromLongest(datas.ambulanceQueueLongest?.date);
        if (ambulanceQueueLongest > 60) innerLevel.ambulanceWait = ESCALATION_LEVEL.extreme;
        else if (ambulanceQueueLongest > 30) innerLevel.ambulanceWait = ESCALATION_LEVEL.severe;
        else if (ambulanceQueueLongest > 20) innerLevel.ambulanceWait = ESCALATION_LEVEL.moderate;

        // Ambulance Corridor Longest
        const ambulanceCorridorLongest = getMinutesFromLongest(datas.ambulanceCohortLongest?.date);
        if (ambulanceCorridorLongest > 60) innerLevel.ambulanceCorridorWait = ESCALATION_LEVEL.extreme;
        else if (ambulanceCorridorLongest > 30) innerLevel.ambulanceCorridorWait = ESCALATION_LEVEL.severe;
        else if (ambulanceCorridorLongest > 20) innerLevel.ambulanceCorridorWait = ESCALATION_LEVEL.moderate;

        // Wait Triage
        const waitTriage = getMinutesFromLongest(datas.waitTriageLongest?.date);
        if (waitTriage > 60) innerLevel.waitTriage = ESCALATION_LEVEL.extreme;
        else if (waitTriage > 40) innerLevel.waitTriage = ESCALATION_LEVEL.severe;
        else if (waitTriage > 15) innerLevel.waitTriage = ESCALATION_LEVEL.moderate;

        // Wait Triage UTC
        const waitTriageUTC = getMinutesFromLongest(datas.waitTriageUTCLongest?.date);
        if (waitTriageUTC > 60) innerLevel.waitTriageUTC = ESCALATION_LEVEL.extreme;
        else if (waitTriageUTC > 40) innerLevel.waitTriageUTC = ESCALATION_LEVEL.severe;
        else if (waitTriageUTC > 15) innerLevel.waitTriageUTC = ESCALATION_LEVEL.moderate;

        // Ambulance Waiting
        const ambulancePts = datas.ambulanceQueue?.length;
        if (ambulancePts > 4) innerLevel.ambulance = ESCALATION_LEVEL.severe;
        else if (ambulancePts > 2) innerLevel.ambulance = ESCALATION_LEVEL.moderate;

        // Ambulance Corridor waiting
        const ambulanceCorridorPts = datas.ambulanceCorridor?.length;
        if (ambulanceCorridorPts > 4) innerLevel.ambulanceCorridor = ESCALATION_LEVEL.severe;
        else if (ambulanceCorridorPts > 2) innerLevel.ambulanceCorridor = ESCALATION_LEVEL.moderate;

        // Corridor Waiting
        const corridorPts = datas.corridor?.length;
        if (corridorPts > 4) innerLevel.corridor = ESCALATION_LEVEL.severe;
        else if (corridorPts > 2) innerLevel.corridor = ESCALATION_LEVEL.moderate;

        // Waiting to be Seen
        const waitSeen = getMinutesFromLongest(datas.waitingSeenLongest?.date);
        if (waitSeen > 240) innerLevel.waitingSeenTime = ESCALATION_LEVEL.extreme;
        else if (waitSeen > 120) innerLevel.waitingSeenTime = ESCALATION_LEVEL.severe;
        else if (waitSeen > 60) innerLevel.waitingSeenTime = ESCALATION_LEVEL.moderate;
        const waitSeenPts = datas.waitingSeen?.length;
        if (waitSeenPts > 15) innerLevel.waitingSeen = ESCALATION_LEVEL.extreme;
        else if (waitSeenPts > 10) innerLevel.waitingSeen = ESCALATION_LEVEL.severe;
        else if (waitSeenPts > 5) innerLevel.waitingSeen = ESCALATION_LEVEL.moderate;

        // Waiting to be Seen ACA
        const waitSeenACA = getMinutesFromLongest(datas.waitingSeenACALongest?.date);
        if (waitSeenACA > 240) innerLevel.waitingSeenACATime = ESCALATION_LEVEL.extreme;
        else if (waitSeenACA > 120) innerLevel.waitingSeenACATime = ESCALATION_LEVEL.severe;
        else if (waitSeenACA > 60) innerLevel.waitingSeenACATime = ESCALATION_LEVEL.moderate;
        const waitSeenACAPts = datas.waitingSeenACA?.length;
        if (waitSeenACAPts > 15) innerLevel.waitingSeenACA = ESCALATION_LEVEL.extreme;
        else if (waitSeenACAPts > 10) innerLevel.waitingSeenACA = ESCALATION_LEVEL.severe;
        else if (waitSeenACAPts > 5) innerLevel.waitingSeenACA = ESCALATION_LEVEL.moderate;

        // Waiting to be Seen UTC
        const waitSeenUTC = getMinutesFromLongest(datas.waitingSeenUTCLongest?.date);
        if (waitSeenUTC > 240) innerLevel.waitingSeenUTCTime = ESCALATION_LEVEL.extreme;
        else if (waitSeenUTC > 120) innerLevel.waitingSeenUTCTime = ESCALATION_LEVEL.severe;
        else if (waitSeenUTC > 60) innerLevel.waitingSeenUTCTime = ESCALATION_LEVEL.moderate;
        const waitSeenUTCPts = datas.waitingSeenUTC?.length;
        if (waitSeenUTCPts > 15) innerLevel.waitingSeenUTC = ESCALATION_LEVEL.extreme;
        else if (waitSeenUTCPts > 10) innerLevel.waitingSeenUTC = ESCALATION_LEVEL.severe;
        else if (waitSeenUTCPts > 5) innerLevel.waitingSeenUTC = ESCALATION_LEVEL.moderate;

        // Waiting for bed
        const waitBed = getMinutesFromLongest(datas.waitBedLongest?.date);
        if (waitBed > 60) innerLevel.waitBedTime = ESCALATION_LEVEL.moderate;

        const waitBedPts = datas.waitBed?.length;
        if (waitBedPts > 10) innerLevel.waitBed = ESCALATION_LEVEL.extreme;
        else if (waitBedPts > 7) innerLevel.waitBed = ESCALATION_LEVEL.severe;

        const excludeForGlobal = ['waitTriageUTC'];

        let cloneLevels = _.cloneDeep(innerLevel);
        _.forEach(excludeForGlobal, exclude => {
            delete cloneLevels[exclude];
        });

        // Get global level
        _.forEach(cloneLevels, le => {
            if (getEscalationLevelIndex(le) > getEscalationLevelIndex(global))
                global = le;
        });
        return {global, innerLevel};
    };

    const getMinutesFromLongest = (time) => {
        if (time) {
            if (_.startsWith(time, '+')) return 99999999;
            const timeS = time.split(':');
            const hour = Number(timeS[0]);
            const minute = Number(timeS[1]);
            return (hour * 60) + minute;
        }

        return 0;
    };

    const patientInZones = (zoneNames, pt, hospital) => {
        let ptBed = pt?.bed || 'wlist1';
        let check = false;

        const zonesF = _.filter(formattedZones, z => {
            if (hospital) return zoneNames.includes(z.areaTitle) && z.hospital === hospital;
            return zoneNames.includes(z.areaTitle);
        });

        _.forEach(zonesF, z => {
            if (check) return;
            const zoneBeds = z.beds.filter(bed => bed !== undefined);
            check = zoneBeds.includes(ptBed);
        });

        return check;
    };

    const getBedsFromZones = (zoneNames) => {
        const beds = [];
        const zonesF = _.filter(formattedZones, z => zoneNames.includes(z.areaTitle));
        _.forEach(zonesF, z => beds.push(...z.beds.filter(bed => bed !== undefined)));
        return beds;
    };

    const getEscalationLevelIndex = (level) => _.findIndex(Object.keys(ESCALATION_LEVEL), rl => rl === level);

    const getEscalationLevelText = (level) => {
        // eslint-disable-next-line default-case
        switch (level) {
            case 'normal':
                return 'EDEL 1 Normal Demand';
            case 'moderate':
                return 'EDEL 2 Moderate Pressure';
            case 'severe':
                return 'EDEL 3 Severe Pressure';
            case 'extreme':
                return 'EDEL 4 Extreme Pressure';
        }
    };

    // Get Obs zone from Rbh
    const getObsBeds = useCallback(() => {
        const obsZoneBeds = [];
        _.filter(zones, z => _.startsWith(z.dataName, 'Obs_')).map(el => obsZoneBeds.push(...el.beds));

        return obsZoneBeds.map(bed => bed?.parsed?.params[1]);
    }, [zones]);

    const checkLongestAwait = (obj, pt) => {
        if (!obj?.id) {
            obj.id = pt.__id;
            obj.date = pt?.admission?.startDate;
        } else {
            if (moment(pt?.admission?.startDate).isBefore(moment(obj.date))) {
                obj.id = pt.__id;
                obj.date = pt?.admission?.startDate;
            }
        }
    };

    const checkLongestBedAwait = (obj, pt) => {
        const getTime = pt?.studies?.bedRequest?.requestedWhen
            ? pt?.studies?.bedRequest?.requestedWhen
            : pt?.studies?.bedRequest?.allocatedWhen;

        if (!obj?.id) {
            obj.id = pt.__id;
            obj.date = getTime;
        } else {
            if (moment(getTime).isBefore(moment(obj.date))) {
                obj.id = pt.__id;
                obj.date = getTime;
            }
        }
    };

    const transformPrettyDateFormat = (obj, numberObj) => {
        if (obj.date && obj.id) {
            obj.date = transformDate(moment().diff(obj.date, 'minutes'));
        }
        if (numberObj) {
            return transformDate(obj);
        }
    };

    const transformDate = (diff) => {
        const hours = Math.floor(diff / 60);
        if (hours >= 100) {
            return '+99:00';
        }

        const hoursText = hours >= 10 ? hours : `0${hours}`;
        const min = Math.floor(diff - (hours * 60));
        const minsText = diff - (hours * 60) >= 10 ? min : `0${min}`;

        return `${hoursText}:${minsText}`;
    };

    const handleChangeMode = (value) => {
        if (value) {
            setDateToggle(value);
            handleClearRange(true);
        }
    };

    const handleReload = () => {
        if (startRange && endRange && moment(startRange).isSameOrBefore(moment(endRange))) setToggleDate(prev => prev ? prev + 1 : 1);
        else dispatch(setSnackbar({type: 'warning', message: t('Select all dates')}));
    };

    const handleChangeDate = (val, type) => {
        // setDateToggle(null);
        if (type === 'startRange') setStartRange(val);
        else setEndRange(val);
    };

    const handleClearRange = (fromToggle) => {
        setStartRange(null);
        setEndRange(null);
        setToggleDate(null);
        if (!fromToggle) setDateToggle('today');
    };

    const handleOpenPatientsLists = (hospitalId, fieldName) => {
        const pts = _.get(data, `${hospitalId}.${fieldName}`);

        if (_.isArray(pts)) {
            if (_.isEmpty(pts)) openEmptyListNotification();
            else handleOpenList(pts);
        } else if (_.isObject(pts)) {
            const patient = _.find(wardPatientsObject, {__id: pts?.id});
            const list = [];

            if (patient) {
                list.push(patient);
                handleOpenList(list);
            } else {
                openEmptyListNotification();
            }
        } else if (_.isString(pts)) {
            if (fieldName === 'avgTime') handleOpenPatientsLists(hospitalId, 'total');
        } else if (_.isUndefined(pts)) {
            openEmptyListNotification();
        }
    };

    const handleOpenList = (list) => {
        setPatients(list);
        openPriority();
    };

    const handleSelectPatient = (ptId) => {
        if (canOpenPatient) {
            const pt = _.find(wardPatientsObject, {__id: ptId});
            if (pt) {
                setSelectedPatient(pt);
                closePriority();
                openPatientContext();
                dispatch(setPatient(pt.patient));
                dispatch(sendUserAudit(pt, AUDIT_TYPES.OPEN_PATIENT_CARD));
            }
        } else {
            dispatch(setSnackbar({type: 'warning', message: t('User has no permission for open patient record.')}));
        }
    };

    const handleBackToMain = () => {
        return isCentralPageModuleEnabled ? history.push(CENTRAL_PAGE_PATH) :
            history.push('/main');
    };

    const renderEscalationLevels = () => {
        function renderBlock(hospital) {
            return (
                <>
                    <Grid item xs={12} className={classes.topStatisticSubtitle}>
                        {_.toUpper(hospital)}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        className={clsx(
                            classes.statisticBlock, 'top',
                            classes.escalationLevel, globalEscalationLevel[hospital],
                        )}
                    >
                        {getEscalationLevelText(globalEscalationLevel[hospital])}
                    </Grid>
                </>
            );
        }

        const width = hospitals?.length > 2;

        return (
            <>
                <div className={classes.topStatisticTitle}>
                    {t('Emergency Department Escalation Levels')}
                </div>

                <Grid container dir="row" justifyContent="center" spacing={2}>
                    {hospitals.map(hospital => (
                        <Grid item xs={width ? 4 : 6} key={hospital?.mapPath}>
                            {renderBlock(hospital?.shortName?.toLowerCase())}
                        </Grid>
                    ))}
                </Grid>
            </>
        );
    };

    const getTotalBridgedCount = (data) => {
        const total = data?.performance4hCountTotal || 0;
        const notBreach = data?.performance4hTotal || 0;
        return total - notBreach;
    };

    const getTotalBridge = (data) => {
        let total = 0;
        let notBreach = 0;
        let percentage = 100;

        total = (data?.rbh?.performance4hCountTotal || 0) + (data?.pht?.performance4hCountTotal || 0);
        notBreach = (data?.rbh?.performance4hTotal || 0) + (data?.pht?.performance4hTotal || 0);
        percentage = ((notBreach / total) * 100).toFixed(2);
        
        return percentage;
    };


    const renderFull4hrPerformance = () => {
        let totalBridgedPHT = getTotalBridgedCount(dashboardStats?.pht);
        let totalBridgedRBH = getTotalBridgedCount(dashboardStats?.rbh);
        const getTotalUHD = getTotalBridge(dashboardStats)

        const pht = dashboardStats?.pht?.performance4h;
        const rbh = dashboardStats?.rbh?.performance4h;

        const data = {
            pht,
            rbh,
            all: getTotalUHD,
            phtCnt: totalBridgedPHT,
            rbhCnt: totalBridgedRBH,
            allCnt: totalBridgedPHT + totalBridgedRBH,
        };

        function getData(filedPath, ignorePercent) {
            const numb = Number(data[filedPath]).toFixed(ignorePercent ? 0 : 2);
            if (isNaN(numb)) return '-';
            return numb + (ignorePercent ? '' : '%');
        }

        function renderBlock(field, title) {
            const width = hospitals?.length > 2 ? '20%' : '30%';
            return (
                <div style={{width, marginRight: 7}}>
                    <Grid item xs={12} className={classes.topStatisticSubtitle}>
                        {title}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        className={clsx(classes.statisticBlock, 'top')}
                    >
                        <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                            {getData(field)}
                        </span>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        style={{marginTop: 10}}
                        className={clsx(classes.statisticBlock, 'top')}
                    >
                        <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                            {getData(field + 'Cnt', true)}
                        </span>
                    </Grid>
                </div>
            );
        }

        return (
            <>
                <div className={classes.topStatisticTitle}>
                    {t('4hr Performance')}
                </div>

                <Grid container dir="row" justifyContent="center">
                    {renderBlock('all', 'UHD')}
                    {hospitals.map(hospital => (
                        <Fragment key={hospital?.mapPath}>
                            {renderBlock(hospital?.shortName?.toLowerCase(), hospital?.shortName)}
                        </Fragment>
                    ))}
                    {}
                </Grid>
            </>
        );
    };

    const renderTopStatistic = (text, field, type) => {
        function getData(hospitalId, field) {
            if (data && data[hospitalId] && data[hospitalId][field]) {
                const value = data[hospitalId][field];

                if (type === 'count') {
                    if (_.isArray(value)) return value.length;
                } else {
                    if (_.isObject(value)) return value?.date || '00:00';
                    if (_.isString(value)) return value;
                }
            }
            return type === 'count' ? 0 : '00:00';
        }

        function renderBlock(hospital) {
            const width = hospitals?.length > 2 ? '30%' : '40%';
            return (
                <div style={{width, marginRight: 7}}>
                    <Grid item xs={12} className={classes.topStatisticSubtitle}>
                        {_.toUpper(hospital)}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        className={clsx(classes.statisticBlock, 'top')}
                        onClick={handleOpenPatientsLists.bind(this, hospital, field)}
                    >
                        <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                            {getData(hospital, field)}
                        </span>
                    </Grid>
                </div>
            );
        }

        return (
            <>
                <div className={classes.topStatisticTitle}>
                    {text}
                </div>

                <Grid container dir="row" justifyContent="center">
                    {hospitals.map(hospital => (
                        <Fragment key={hospital?.mapPath}>
                            {renderBlock(hospital?.shortName?.toLowerCase())}
                        </Fragment>
                    ))}
                </Grid>
            </>
        );
    };

    const renderChart = (text, field, ambulance) => {
        const colors = [
            {borderColor: 'rgb(164,255,152)', backgroundColor: 'rgb(214,255,208)'},
            {borderColor: 'rgb(101,197,255)', backgroundColor: 'rgb(175,225,255)'},
            {borderColor: 'rgb(255,165,0)', backgroundColor: 'rgb(255,223,186)'},
        ];

        function renderHospitalsChart() {
            if (chartData) {
                const size = chartData?.pht?.[field] ? Object.keys(chartData?.pht?.[field]).length : 12;
                let format = size > 24 ? 'DD/MM' : 'hh A';
                const labels = chartData?.pht ? Object.keys(chartData?.pht?.[field]).map(time => moment(time).format(format)) : [];
                const lineWidth = extendedDashboardLineWidth;
                const datasets = [];

                hospitals.forEach((hospital, index) => {
                    const idHos = hospital?.shortName?.toLowerCase();
                    datasets.push({
                        label: hospital?.shortName,
                        data: chartData?.[idHos] ? Object.keys(chartData?.[idHos]?.[field]).map(data => chartData?.[idHos]?.[field][data]) : labels.map(() => 0),
                        scaleFontColor: '#FFFFFF',
                        pointLabelFontColor: '#FFFFFF',
                        borderColor: colors[index].borderColor,
                        backgroundColor: colors[index].backgroundColor,
                        borderWidth: lineWidth,
                        lineTension: 0.4,
                    });
                });

                const dataForChart = {
                    labels,
                    datasets,
                };
                const chartOption = {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            labels: {
                                color: theme.palette.mode === 'light' ? 'black' : 'white',
                            },
                        },
                    },
                    line: {
                        tension: 0.4,
                    },
                    scales: {
                        x: {
                            ticks: {
                                color: theme.palette.mode === 'light' ? 'black' : 'white',
                            },
                        },
                        y: {
                            beginAtZero: true,
                            ticks: {
                                color: theme.palette.mode === 'light' ? 'black' : 'white',
                                precision: 0,
                            },
                        },
                    },
                };

                return (
                    <div style={{height: 200}}>
                        <Line options={chartOption} data={dataForChart}/>
                    </div>
                );
            }
        }

        function renderChartStats(hospital) {
            function getData(type) {
                const value = chartData?.[hospital]?.[`${type}${ambulance ? 'Ambulance' : ''}`];
                return value || 0;
            }

            return (
                <>
                    <div className={classes.rowDiv}>
                        <span className={classes.statisticTitle}>
                            {t('Since Midnight')}
                        </span>

                        <div className={clsx(classes.statisticBlock, 'top', classes.chartBlock)}>
                            <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                                {getData('midnight')}
                            </span>
                        </div>
                    </div>

                    <div className={classes.rowDiv}>
                        <span className={classes.statisticTitle}>
                            {t('Last Hour')}
                        </span>

                        <div className={clsx(classes.statisticBlock, 'top', classes.chartBlock)}>
                            <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                                {getData('lastHour')}
                            </span>
                        </div>
                    </div>
                </>
            );
        }

        const width = hospitals?.length > 2 ? 4 : 6;

        return (
            <div style={{marginTop: 5}}>
                <span className={classes.chartText}>
                    {text}
                </span>

                <div className={classes.chart}>
                    {renderHospitalsChart()}
                </div>

                <Grid container direction="row" spacing={2}>
                    {hospitals.map((hospital) => (
                        <Grid item xs={width} style={{textAlign: 'center'}} key={hospital?.mapPath}>
                            {hospital?.shortName}
                        </Grid>
                    ))}

                    {hospitals.map((hospital) => (
                        <Grid item xs={width} key={hospital?.mapPath}>
                            {renderChartStats(hospital?.shortName?.toLowerCase())}
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    };

    const renderStatisticTopic = () => {
        const width = hospitals?.length > 2 ? 2 : 3;
        return (
            <Grid item xs={12}>
                <Grid
                    container
                    direction="row"
                    spacing={1}
                    style={{fontWeight: 'bold', marginBottom: 5}}
                >
                    <Grid item xs={6}/>
                    {hospitals.map((hospital) => (
                        <Grid
                            key={hospital?.mapPath}
                            item
                            xs={width}
                            className={classes.statisticTopic}
                        >
                            {hospital?.shortName}
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        );
    };

    const renderCapacity = () => {
        function getData(hospitalId, filedPath) {
            if (data && data[hospitalId] && data[hospitalId]['cap'][filedPath]) {
                return data[hospitalId]['cap'][filedPath];
            }
            return 0;
        }

        function renderBlock(hospital, fieldPath, isTop) {
            const count = getData(hospital, fieldPath);
            return (
                <div className={clsx(classes.statisticBlock, isTop ? 'top' : 'bottom', count < 0 && classes.redButton)}
                     style={{cursor: 'auto'}}>
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                        {count}
                    </span>
                </div>
            );
        }

        const renderInnerCapacity = (text, field) => {
            const width = hospitals?.length > 2 ? 2 : 3;

            return (
                <Grid item xs={12}>
                    <Grid
                        container
                        direction="row"
                        alignItems="end"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle}>
                                {text}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), field, true)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            );
        };

        return (
            <>
                <Grid item xs={12} style={{textAlign: 'center'}}>
                    <span className={classes.statisticTitle}>
                        {t('Current Capacity')}
                    </span>
                </Grid>

                <Grid item xs={12} style={{marginBottom: 5}}>
                    {renderInnerCapacity('Majors', 'majors')}
                </Grid>
                <Grid item xs={12} style={{marginBottom: 5}}>
                    {renderInnerCapacity('ACA', 'aca')}
                </Grid>
                <Grid item xs={12} style={{marginBottom: 5}}>
                    {renderInnerCapacity('Paeds', 'paeds')}
                </Grid>
                <Grid item xs={12} style={{marginBottom: 5}}>
                    {renderInnerCapacity('Resus', 'resus')}
                </Grid>
            </>
        );
    };

    const renderReferred = () => {
        function getData(hospitalId, filedPath) {
            if (data && data[hospitalId] && data[hospitalId]['referredTo'][filedPath]) {
                return data[hospitalId]['referredTo'][filedPath]?.length || 0;
            }
            return 0;
        }

        function renderBlock(hospital, fieldPath, isTop) {
            return (
                <div
                    onClick={handleOpenPatientsLists.bind(this, hospital, `referredTo.${fieldPath}`)}
                    className={clsx(classes.statisticBlock, isTop ? 'top' : 'bottom')}
                >
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                        {getData(hospital, fieldPath)}
                    </span>
                </div>
            );
        }

        const renderReferral = (ref) => {
            const width = hospitals?.length > 2 ? 2 : 3;

            return (
                <Grid item xs={12}>
                    <Grid container direction="row" alignItems="end" spacing={1}>
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle}>
                                {ref}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), ref, true)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            );
        };

        return (
            <>
                <Grid item xs={12} style={{textAlign: 'center'}}>
                    <span className={classes.statisticTitle}>
                        {t('Referred To')}
                    </span>
                </Grid>

                {referredOpt.map(ref => (
                    <Grid key={ref.id} item xs={12} style={{marginBottom: 5}}>
                        {renderReferral(ref.name)}
                    </Grid>
                ))}

                <Grid item xs={12}>
                    <Divider style={{margin: '10px 0'}}/>
                </Grid>
            </>
        );
    };

    const renderStatisticTime = (text, field, fromAPI, customClick, isMinutes) => {
        function getData(hospitalId, filedPath) {
            if (fromAPI) {
                if (dashboardStats && dashboardStats[hospitalId] && dashboardStats[hospitalId][filedPath]) {
                    const time = dashboardStats[hospitalId][filedPath];
                    if (time > 0) {
                        return isMinutes ? transformDate(time) : transformDate(time / 60);
                    }
                }
            } else if (data && data[hospitalId] && data[hospitalId][filedPath]) {
                return data[hospitalId][filedPath] || '00:00';
            }
            return '00:00';
        }

        const handleOpenPatientsLists = (hospitalId, fieldName) => {
            const field = customClick ? customClick : fieldName;
            const pts = _.get(data, `${hospitalId}.${field}`);

            if (_.isArray(pts)) {
                if (_.isEmpty(pts)) openEmptyListNotification();
                else handleOpenList(pts);
            } else if (_.isObject(pts)) {
                const patient = _.find(wardPatientsObject, {__id: pts?.id});
                const list = [];

                if (patient) {
                    list.push(patient);
                    handleOpenList(list);
                } else {
                    openEmptyListNotification();
                }
            } else if (_.isString(pts)) {
                if (fieldName === 'avgTime') handleOpenPatientsLists(hospitalId, 'total');
            } else if (_.isUndefined(pts)) {
                openEmptyListNotification();
            }
        };

        function renderBlock(hospital, fieldPath, isTop) {
            return (
                <div
                    onClick={!fromAPI ? handleOpenPatientsLists.bind(this, hospital, fieldPath) : null}
                    className={clsx(classes.statisticBlock, isTop ? 'top' : 'bottom')}
                    style={{cursor: fromAPI ? 'auto' : 'pointer'}}
                >
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>{
                        getData(hospital, fieldPath)}
                    </span>
                </div>
            );
        }

        const width = hospitals?.length > 2 ? 2 : 3;
        return (
            <>
                <Grid item xs={12}>
                    <Grid container direction="row" alignItems="end" spacing={1}>
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle}>
                                {text}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), field, true)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Divider style={{margin: '10px 0'}}/>
                </Grid>
            </>
        );
    };

    const renderStatistic = (text, bottomText, field, fieldLongest, escalationLvl, longestEscalationLevel, fromAPI, apiType, disableHospital) => {
        function getData(hospitalId, filedPath) {
            if (fromAPI) {
                if (dashboardStats && dashboardStats[hospitalId] && dashboardStats[hospitalId][filedPath]) {
                    const val = dashboardStats[hospitalId][filedPath];

                    if (apiType === 'percent') {
                        const numb = Number(val).toFixed(2);
                        return numb + '%';
                    } else {
                        return val;
                    }
                }
            } else if (data && data[hospitalId] && data[hospitalId][filedPath]) {
                const value = data[hospitalId][filedPath];
                if (_.isArray(value)) return value.length;
                if (_.isObject(value)) return value.date || '00:00';
                if (_.isString(value)) return value;
            }
            return 0;
        }

        function renderBlock(hospital, fieldPath, isTop, escalation) {
            const escalationFiled = _.get(escalationLevel, `${hospital}.${escalation}`);
            const isDisabled = hospital === disableHospital;
            
            return (
                <div
                    onClick={(!fromAPI && !isDisabled) ? handleOpenPatientsLists.bind(this, hospital, field) : null}
                    className={clsx(
                        classes.statisticBlock, isTop ? 'top' : 'bottom',
                        (escalation && !isDisabled) && classes.escalationLevel, escalationFiled,
                        isDisabled && classes.disabledField,
                    )}
                    style={{cursor: (fromAPI || isDisabled) ? 'auto' : 'pointer'}}
                >
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>
                        {isDisabled ? '-' : getData(hospital, fieldPath)}
                    </span>
                </div>
            );
        }

        const width = hospitals?.length > 2 ? 2 : 3;

        return (
            <>
                <Grid item xs={12}>
                    <Grid container direction="row" alignItems="end" spacing={1}>
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle}>
                                {text}
                            </span>
                        </Grid>

                        {hospitals?.map(hospital => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), field, true, escalationLvl)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {fieldLongest &&
                    <Grid item xs={12} className={classes.separator}>
                        <Grid
                            container
                            direction="row"
                            alignItems="start"
                            spacing={1}
                        >
                            <Grid item xs={6} style={{textAlign: 'right'}}>
                                <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                    {bottomText}
                                </span>
                            </Grid>

                            {hospitals?.map(hospital => (
                                <Grid item xs={width} key={hospital?.mapPath}>
                                    {renderBlock(hospital?.shortName?.toLowerCase(), fieldLongest, false, longestEscalationLevel)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                }

                <Grid item xs={12}>
                    <Divider style={{margin: '10px 0'}}/>
                </Grid>
            </>
        );
    };

    const renderStatistic4hr = () => {
        function getData(hospitalId, filedPath, notInBreachCountFieldPath, totalCountFieldPath) {
            if (dashboardStats && dashboardStats[hospitalId] && dashboardStats[hospitalId][filedPath] !== undefined
                && dashboardStats[hospitalId][filedPath] !== null) {
                const val = dashboardStats[hospitalId][filedPath];

                const valNIB = dashboardStats[hospitalId][notInBreachCountFieldPath];
                const valTOT = dashboardStats[hospitalId][totalCountFieldPath] || 0;
                if (_.isNumber(valNIB) && _.isNumber(valTOT)) {
                    const numbNIB = Number(valNIB).toFixed(0);
                    const numbTOT = Number(valTOT).toFixed(0);

                    return `${numbTOT - numbNIB}`;
                } else {
                    const numb = Number(val).toFixed(2);
                    return `${numb}%`;
                }
            }
            return 0;
        }

        function renderBlock(hospital, fieldPath, isTop, notInBreachCountFieldPath, totalCountFieldPath) {
            return (
                <div className={clsx(classes.statisticBlock, isTop ? 'top' : 'bottom')}>
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>{
                        getData(hospital, fieldPath, notInBreachCountFieldPath, totalCountFieldPath)}
                    </span>
                </div>
            );
        }

        const width = hospitals?.length > 2 ? 2 : 3;

        return (
            <>
                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {t('4hr Performance Majors')}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), 'performance4hMajors', false)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {t('4hr Breaches Majors')}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), 'performance4hMajors', false, 'performance4hCountMajors', 'performance4hCountTotalMajors')}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {t('4hr Performance ACA')}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), 'performance4hACA', false)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {t('4hr Breaches ACA')}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), 'performance4hACA', false, 'performance4hCountACA', 'performance4hCountTotalACA')}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {t('4hr Performance UTC')}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), 'performance4hUTC', false)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {t('4hr Breaches UTC')}
                            </span>
                        </Grid>

                        {hospitals.map((hospital) => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), 'performance4hUTC', false, 'performance4hCountUTC', 'performance4hCountTotalUTC')}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Divider style={{margin: '10px 0'}}/>
                </Grid>
            </>
        );
    };

    const render3GroupStatistic = (firstText, middleText, bottomText, firstField, middleField, bottomField, escalationLvl, longestEscalationLevel) => {
        function getData(hospitalId, filedPath) {
            if (data && data[hospitalId] && data[hospitalId][filedPath]) {
                const value = data[hospitalId][filedPath];
                if (_.isArray(value)) return value.length;
                if (_.isObject(value)) return value.date || '00:00';
                if (_.isString(value)) return value;
            }
            return 0;
        }

        function renderBlock(hospital, fieldPath, isTop, escalation, fromApi) {
            const escalationFiled = _.get(escalationLevel, `${hospital}.${escalation}`);
            return (
                <div
                    onClick={handleOpenPatientsLists.bind(this, hospital, fieldPath)}
                    className={clsx(
                        classes.statisticBlock, isTop ? 'top' : 'bottom',
                        escalation && classes.escalationLevel, escalationFiled,
                    )}
                    style={{cursor: 'pointer'}}
                >
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>{
                        getData(hospital, fieldPath, fromApi)}
                    </span>
                </div>
            );
        }

        const width = hospitals?.length > 2 ? 2 : 3;

        return (
            <>
                <Grid item xs={12}>
                    <Grid container direction="row" alignItems="end" spacing={1}>
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle}>
                                {firstText}
                            </span>
                        </Grid>

                        {hospitals?.map(hospital => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), firstField, true)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid container direction="row" alignItems="end" spacing={1}>
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle}>
                                {middleText}
                            </span>
                        </Grid>

                        {hospitals?.map(hospital => (
                            <Grid item xs={width} key={hospital?.mapPath}>
                                {renderBlock(hospital?.shortName?.toLowerCase(), middleField, true, escalationLvl)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12} className={classes.separator}>
                    <Grid
                        container
                        direction="row"
                        alignItems="start"
                        spacing={1}
                    >
                        <Grid item xs={6} style={{textAlign: 'right'}}>
                            <span className={classes.statisticTitle} style={{fontSize: 'small'}}>
                                {bottomText}
                            </span>
                        </Grid>

                        {hospitals?.map(hospital => (
                            <Grid
                                item
                                xs={width}
                                key={hospital?.mapPath}
                                onClick={handleOpenPatientsLists.bind(this, hospital?.shortName?.toLowerCase(), bottomField)}
                            >
                                {renderBlock(hospital?.shortName?.toLowerCase(), bottomField, false, longestEscalationLevel)}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Divider style={{margin: '10px 0'}}/>
                </Grid>
            </>
        );
    };

    return (
        <div className={classes.root}>
            <AgyleHeader onBack={handleBackToMain} loading={loading}>
                <div className={classes.banner}>
                    {t('Emergency Department Live Dashboard')}
                </div>
            </AgyleHeader>

            <Paper className={classes.paper}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <AgyleToggleButtons
                        onChange={handleChangeMode}
                        value={dateToggle}
                        size="medium"
                        options={[
                            {value: 'today', label: 'Today'},
                            {value: '24h', label: 'Since 24h'},
                        ]}
                        minWidth={100}
                    />

                    <div className={classes.rowDiv}>
                        <FormLabel className={classes.label}>
                            {t('Date Range')}
                        </FormLabel>

                        <div style={{marginLeft: 10}}>
                            <AgyleDatePicker
                                label={t('From')}
                                onChange={val => handleChangeDate(val, 'startRange')}
                                value={startRange}
                                type="dateTime"
                                maxDate={moment()}
                            />
                        </div>

                        <div style={{marginLeft: 10}}>
                            <AgyleDatePicker
                                label={t('To')}
                                onChange={val => handleChangeDate(val, 'endRange')}
                                value={endRange}
                                type="dateTime"
                                maxDate={moment()}
                                minDate={startRange}
                            />
                        </div>

                        <AgyleButton
                            onClick={handleReload}
                            style={{marginLeft: 10}}
                        >
                            {t('Apply')}
                        </AgyleButton>

                        <IconButton
                            disableRipple
                            onClick={handleClearRange.bind(this, false)}
                            size="small"
                        >
                            <ClearIcon fontSize="small"/>
                        </IconButton>
                    </div>
                </Grid>
            </Paper>

            <Paper className={clsx(classes.paper, 'scroll-normal-white')}>
                <Grid container direction="row" className={classes.topStatisticGroup}>
                    <Grid item xs={12} sx={{display: {lg: 'none', md: 'block'}}}>
                        {renderEscalationLevels()}
                    </Grid>

                    <Grid item lg={2} md={6} xs={12}>
                        {renderFull4hrPerformance()}
                    </Grid>

                    <Grid item lg={2} md={6} xs={12}>
                        {renderTopStatistic('Total in dept', 'total', 'count')}
                    </Grid>

                    <Grid item lg={4} sx={{display: {lg: 'block', xs: 'none'}}}>
                        {renderEscalationLevels()}
                    </Grid>

                    <Grid item lg={2} md={6} xs={12}>
                        {renderTopStatistic('Over 12 hrs', 'over12', 'count')}
                    </Grid>

                    <Grid item lg={2} md={6} xs={12}>
                        {renderTopStatistic('ED Longest Time', 'longestPt', 'time')}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                    >
                        <Grid item lg={3} md={6} xs={12} sx={{zIndex: 50}}>
                            <div className={classes.tittle}>
                                {t('Flow In')}
                            </div>

                            <div className={classes.statisticContainer}>
                                {renderChart('Arrivals per hour running total', 'arrivals')}
                                {renderChart('Ambulance arrivals per hour running total', 'ambulanceArrivals', true)}
                            </div>
                        </Grid>

                        <Grid item lg={3} md={6} xs={12} sx={{zIndex: 50}}>
                            <div className={classes.tittle}>
                                {t('Waiting')}
                            </div>

                            <div className={classes.statisticContainer}>
                                {renderStatisticTopic()}
                                {renderStatistic('ED Waiting Room', 'Longest Wait', 'waitingRoom', 'waitingRoomLongest')}
                                {renderStatistic('Awaiting Triage', 'Longest Wait', 'waitTriage', 'waitTriageLongest', null, 'waitTriage')}
                                {renderStatistic('Ambulances Waiting', 'Longest Wait', 'ambulanceQueue', 'ambulanceQueueLongest', 'ambulance', 'ambulanceWait')}
                                {renderStatistic('Ambulance Corridor', 'Longest Wait', 'ambulanceCohort', 'ambulanceCohortLongest', 'ambulanceCorridor', 'ambulanceCorridorWait')}
                                {renderStatisticTime('Hours Lost', 'hourLostTime', true, null, true)}
                                {renderStatisticTime('Avg. Handover Time', 'avgHandoverTime', true)}
                                {renderStatistic('ED Corridor', 'Longest Wait', 'corridor', 'corridorLongest', 'corridor')}
                                {renderStatistic('Awaiting Triage UTC', 'Longest Wait', 'waitTriageUTC', 'waitTriageUTCLongest', null, 'waitTriageUTC', null, null, 'rbh')}
                            </div>
                        </Grid>

                        <Grid item lg={3} md={6} xs={12} sx={{zIndex: 50}}>
                            <div className={classes.tittle}>
                                {t('Clinical Process')}
                            </div>

                            <div className={classes.statisticContainer}>
                                {renderStatisticTopic()}
                                {render3GroupStatistic('Total in Majors', 'Waiting to be seen in Majors', 'Longest wait to be seen', 'totalMajors', 'waitingSeen', 'waitingSeenLongest', 'waitingSeen', 'waitingSeenTime')}
                                {render3GroupStatistic('Total in ACA', 'Waiting to be seen ACA', 'Longest Wait to be seen', 'totalACA', 'waitingSeenACA', 'waitingSeenACALongest', 'waitingSeenACA', 'waitingSeenACATime')}
                                {renderStatistic('Awaiting Diagnosis', 'Longest Diagnosis Time', 'delayed', 'delayedLongest')}
                                {render3GroupStatistic('Total in UTC', 'Waiting to be seen UTC', 'Longest Wait to be seen', 'totalUTC', 'waitingSeenUTC', 'waitingSeenUTCLongest', 'waitingSeenUTC', 'waitingSeenUTCTime')}
                                {renderCapacity()}
                            </div>
                        </Grid>

                        <Grid item lg={3} md={6} xs={12} sx={{zIndex: 50}}>
                            <div className={classes.tittle}>
                                {t('Flow Out')}
                            </div>

                            <div className={classes.statisticContainer}>
                                {renderStatisticTopic()}
                                {renderStatistic4hr()}
                                {renderStatistic('Waiting for Beds', 'Longest Bed Wait', 'waitBed', 'waitBedLongest', 'waitBed', 'waitBedTime')}
                                {renderStatisticTime('Avg. Diagnosis Time Majors', 'avgDecisionTimeMajors', true)}
                                {renderStatisticTime('Avg. Diagnosis Time UTC', 'avgDecisionTimeUTC', true)}
                                {renderReferred()}
                                {renderStatistic('Left Without being Seen', null, 'withoutSeen', null, null, null, true)}
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            <AgyleDialog
                title={'Patient List'}
                open={isPriorityOpen}
                onClose={closePriority}
                width={'xl'}
                fullWidth
                disableSave
            >
                <PriorityPatients
                    simpleMode
                    patients={patients}
                    selectPatient={handleSelectPatient}
                    zones={zones}
                    onClose={closePriority}
                />
            </AgyleDialog>

            {isPatientContextOpen &&
                <ContextViewer
                    currentPatient={selectedPatient}
                    contextOpen={isPatientContextOpen}
                    closeContext={closePatientContext}
                    idElement="current"
                    arrivalMode={ambulanceCodes}
                    // isSameName={isMultiSameNamePt}
                />
            }

            <MessageBox
                title={t('Information')}
                haveAccept={{no: t('Close')}}
                onClose={closeEmptyListNotification}
                open={isEmptyListNotificationOpen}
                message={t('There are no patients to display.')}
            />

            <div ref={refContainer}/>
        </div>
    );
};

export default ExtendedDashboard;